import * as React from 'react';
import {
  Box,
  NativeSelect,
  type NativeSelectRootProps,
} from '@chakra-ui/react';

/**
 * Project Select.
 *
 * NB-shaped facade over Chakra v3's `NativeSelect` (renders a real
 * `<select>` element). Bridges:
 *   - NB `selectedValue` -> `<select>.value`
 *   - NB `onValueChange(value)` -> `onChange(e)` with `e.target.value`
 *   - `<Select.Item label value />` -> `<option value>{label}</option>`
 */

export type SelectProps = NativeSelectRootProps & {
  selectedValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  iconLeft?: React.ReactElement;
};

const SelectRoot = ({
  selectedValue,
  onValueChange,
  placeholder,
  iconLeft,
  children,
  ...rest
}: SelectProps) => (
  <NativeSelect.Root {...rest}>
    <NativeSelect.Field
      value={selectedValue ?? ''}
      onChange={(e) => onValueChange?.(e.currentTarget.value)}
      paddingInlineStart={iconLeft ? '8' : undefined}
    >
      {placeholder !== undefined && (
        <option value="" disabled hidden>
          {placeholder}
        </option>
      )}
      {children}
    </NativeSelect.Field>
    {iconLeft && (
      <Box
        position="absolute"
        left="3"
        top="50%"
        transform="translateY(-50%)"
        pointerEvents="none"
        color="inherit"
      >
        {iconLeft}
      </Box>
    )}
    <NativeSelect.Indicator />
  </NativeSelect.Root>
);

type ItemProps = {
  label: string;
  value: string;
};

const Item = ({ label, value }: ItemProps) => (
  <option value={value}>{label}</option>
);

export const Select = Object.assign(SelectRoot, { Item });
